import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Signal,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  CONFIGURATION_SETTING_KEYS,
  EAddressValidationType,
  EEntityType,
  ERelationType,
  EZipCodeValidation,
  ICity,
  ICompleteOnboardingDto,
  ICountry,
  INormalText,
  IStreet,
  IUserOnboardingForm,
} from '@web/dtos';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { Dialog, IDialogData } from '../dialog';
import { DialogComponent } from '../dialog/dialog.component';
import { OnboardingStore } from '../../../services/api/user/onboarding/onboarding.store';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { ConfigurationStore } from '../../../services/api/configuration/configuration-store.service';
import { SalutationStore } from '../../../services/api/salutation/salutation-store.service';
import { isDate } from '../../../validators/isDate';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { ToFontSizePipe } from '../../../pipes/to-font-size/to-font-size.pipe';
import { ToTranslatedOptionPipe } from '../../../pipes/to-select-option/to-translated-option.pipe';
import { TextInputComponent } from '../../data-input/form-inputs/text-input/text-input.component';
import { DateInputComponent } from '../../data-input/form-inputs/date-input/date-input/date-input.component';
import { DropDownComponent } from '../../data-input/form-inputs/drop-down/drop-down.component';
import { CheckboxComponent } from '../../data-input/form-inputs/checkbox/checkbox.component';
import { ButtonComponent } from '../../feedback/button/button.component';
import { LoadingAnimationComponent } from '../../feedback/loading-animation/loading-animation.component';
import { CountryStore } from '../../../services/api/address-validation/county/country-store.service';
import { ERegisterOptions } from '../../../constants/buttonGroups.enums';
import { ButtonGroupComponent } from '../../data-input/form-inputs/button-group/button-group.component';
import { EnumToButtonOptionArrayPipe } from '../../../pipes/enum-to-button-option-array/enum-to-button-option-array.pipe';
import { AutoCompleteComponent } from '../../data-input/form-inputs/auto-complete/auto-complete.component';
import { CityStore } from '../../../services/api/address-validation/city/city-store.service';
import { StreetStore } from '../../../services/api/address-validation/street/street-store.service';

const ADDRESS_SEARCH_DEBOUNCE_MS = 300;

@Component({
  selector: 'aw-user-onboarding-dialog',
  imports: [
    ReactiveFormsModule,
    DialogComponent,
    FilterTextsPipe,
    LanguagePipe,
    ToFontSizePipe,
    ToTranslatedOptionPipe,
    TextInputComponent,
    DateInputComponent,
    DropDownComponent,
    CheckboxComponent,
    ButtonGroupComponent,
    ButtonComponent,
    LoadingAnimationComponent,
    EnumToButtonOptionArrayPipe,
    AutoCompleteComponent,
  ],
  templateUrl: './user-onboarding-dialog.component.html',
  styles: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserOnboardingDialogComponent extends Dialog<IDialogData> {
  protected readonly onboardingStore: OnboardingStore = inject(OnboardingStore);
  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly salutationStore: SalutationStore = inject(SalutationStore);
  protected readonly countryStore: CountryStore = inject(CountryStore);
  protected readonly cityStore: CityStore = inject(CityStore);
  protected readonly streetStore: StreetStore = inject(StreetStore);
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;
  protected readonly ERegisterOptions: typeof ERegisterOptions =
    ERegisterOptions;
  protected readonly salutationOptions: Signal<INormalText[]> =
    this.salutationStore.salutationOptions;
  protected readonly availableCountries: Signal<ICountry[]> =
    this.countryStore.countries;
  protected readonly availableCities: Signal<ICity[]> = this.cityStore.cities;
  protected readonly availableStreets: Signal<IStreet[]> =
    this.streetStore.streets;

  protected readonly addressValidationType: Signal<EAddressValidationType> =
    computed(
      (): EAddressValidationType =>
        this.configurationStore.getEnumConfiguration(
          CONFIGURATION_SETTING_KEYS.AddressValidationType,
          EAddressValidationType
        )
    );
  protected readonly useFreeAddressInput: Signal<boolean> = computed(
    (): boolean => this.addressValidationType() === EAddressValidationType.None
  );
  protected readonly useLooseAddressValidation: Signal<boolean> = computed(
    (): boolean =>
      this.addressValidationType() === EAddressValidationType.Loose ||
      this.addressValidationType() ===
        EAddressValidationType.LooseWithoutHouseNumber
  );

  protected readonly isExistingCustomerRegistrationActive: Signal<boolean> =
    computed(
      () =>
        this.configurationStore.getStringConfiguration(
          CONFIGURATION_SETTING_KEYS.ExistingCustomerRegistration
        ) !== 'none'
    );

  protected readonly onboardingForm: FormGroup<IUserOnboardingForm> =
    this.formBuilder.group({
      customerTypeSelection: this.formBuilder.nonNullable.control<string>(
        ERegisterOptions['Ich bin Neukunde'],
        Validators.required
      ),
      salutation: this.formBuilder.nonNullable.control('', Validators.required),
      firstName: this.formBuilder.nonNullable.control('', Validators.required),
      lastName: this.formBuilder.nonNullable.control('', Validators.required),
      birthDate: this.formBuilder.nonNullable.control('', [
        Validators.required,
        isDate(),
      ]),
      phoneNumber: this.formBuilder.nonNullable.control(
        '',
        Validators.required
      ),
      country: this.formBuilder.nonNullable.control('', Validators.required),
      postCode: this.formBuilder.nonNullable.control('', Validators.required),
      city: this.formBuilder.nonNullable.control('', Validators.required),
      street: this.formBuilder.nonNullable.control('', Validators.required),
      houseNumber: this.formBuilder.nonNullable.control(
        '',
        this.addressValidationType() ===
          EAddressValidationType.LooseWithoutHouseNumber
          ? []
          : Validators.required
      ),
      additional: this.formBuilder.nonNullable.control(''),
      confirmAddressIsAccurate: this.formBuilder.nonNullable.control(false),
      termsAccepted: this.formBuilder.nonNullable.control(false, [
        Validators.requiredTrue,
      ]),
    });

  private readonly onboardingFormValue: Signal<
    FormGroup<IUserOnboardingForm>['value']
  > = toSignal(this.onboardingForm.valueChanges, {
    initialValue: this.onboardingForm.getRawValue(),
  });

  protected readonly isAddressLookupLoading: Signal<boolean> = computed(
    (): boolean => this.cityStore.isLoading() || this.streetStore.isLoading()
  );

  protected readonly hasUnverifiedAddressInput: Signal<boolean> = computed(
    (): boolean => {
      if (
        this.useFreeAddressInput() ||
        !this.isAddressComplete() ||
        this.isAddressLookupLoading()
      ) {
        return false;
      }

      const value = this.onboardingFormValue();
      const cityIsKnown = this.isKnownOption(
        this.availableCities(),
        value.city,
        'city'
      );
      const streetIsKnown = this.isKnownOption(
        this.availableStreets(),
        value.street,
        'street'
      );

      return !cityIsKnown || !streetIsKnown;
    }
  );

  protected readonly showAddressConfirmationBox: Signal<boolean> = computed(
    (): boolean =>
      this.useLooseAddressValidation() && this.hasUnverifiedAddressInput()
  );

  protected readonly showAddressWarning: Signal<boolean> = computed(
    (): boolean =>
      this.addressValidationType() === EAddressValidationType.Strict &&
      this.hasUnverifiedAddressInput()
  );

  protected readonly earliestValidBirthDay: Signal<Date> = computed(() => {
    const minimumAge = this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.MinimumRegistrationAge
    );
    const today = new Date();

    return new Date(
      today.getFullYear() - minimumAge,
      today.getMonth(),
      today.getDate()
    );
  });

  constructor() {
    super();

    this.onboardingForm.controls.country.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((countryCode: string): void => {
        this.resetControls(['postCode', 'city', 'street', 'houseNumber']);
        this.cityStore.reset();
        this.streetStore.reset();
        this.updatePostalCodeValidators(countryCode);
      });

    this.onboardingForm.controls.postCode.valueChanges
      .pipe(
        debounceTime(ADDRESS_SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((postCode: string): void => {
        this.resetControls(['city', 'street', 'houseNumber']);
        this.cityStore.reset();
        this.streetStore.reset();

        const countryCode = this.onboardingForm.controls.country.value;
        if (
          !this.useFreeAddressInput() &&
          countryCode &&
          postCode &&
          this.onboardingForm.controls.postCode.valid
        ) {
          this.cityStore.loadCities({ countryCode, postCode });
        }
      });

    this.onboardingForm.controls.city.valueChanges
      .pipe(
        debounceTime(ADDRESS_SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((city: string): void => {
        this.resetControls(['street', 'houseNumber']);
        this.streetStore.reset();

        const { country, postCode } = this.onboardingForm.getRawValue();
        if (!this.useFreeAddressInput() && country && postCode && city) {
          this.streetStore.loadStreets({
            countryCode: country,
            postCode,
            city,
          });
        }
      });

    this.onboardingForm.controls.street.valueChanges
      .pipe(
        debounceTime(ADDRESS_SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((): void => {
        this.onboardingForm.controls.houseNumber.reset();
      });

    effect((): void => {
      this.availableCities();
      this.onboardingForm.controls.city.updateValueAndValidity({
        emitEvent: false,
      });
    });

    effect((): void => {
      this.availableStreets();
      this.onboardingForm.controls.street.updateValueAndValidity({
        emitEvent: false,
      });
    });

    effect((): void => {
      const confirmationControl =
        this.onboardingForm.controls.confirmAddressIsAccurate;

      if (this.showAddressConfirmationBox()) {
        confirmationControl.addValidators(Validators.requiredTrue);
      } else {
        confirmationControl.removeValidators(Validators.requiredTrue);
        confirmationControl.setValue(false, { emitEvent: false });
      }
      confirmationControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  protected isFormReady(): boolean {
    const strictAddressIsValid =
      this.addressValidationType() !== EAddressValidationType.Strict ||
      !this.hasUnverifiedAddressInput();

    return (
      this.onboardingForm.valid &&
      !this.onboardingStore.isLoading() &&
      !this.isAddressLookupLoading() &&
      strictAddressIsValid
    );
  }

  protected async completeOnboarding(): Promise<void> {
    if (!this.isFormReady()) {
      this.onboardingForm.markAllAsTouched();
      return;
    }

    const formValue = this.onboardingForm.getRawValue();
    const onboardingData: ICompleteOnboardingDto = {
      salutation: formValue.salutation,
      firstName: formValue.firstName.trim(),
      lastName: formValue.lastName.trim(),
      birthDate: formValue.birthDate,
      phoneNumber: formValue.phoneNumber.trim(),
      address: {
        firstName: formValue.firstName.trim(),
        lastName: formValue.lastName.trim(),
        country: formValue.country.trim(),
        postCode: formValue.postCode.trim(),
        city: formValue.city.trim(),
        street: formValue.street.trim(),
        houseNumber: formValue.houseNumber.trim(),
        additional: formValue.additional.trim(),
      },
      termsAccepted: true,
    };

    await this.onboardingStore.complete(onboardingData);

    if (this.onboardingStore.isCompleted()) {
      this.dialogRef.close();
    }
  }

  private updatePostalCodeValidators(countryCode: string): void {
    const country = this.availableCountries().find(
      (option: ICountry): boolean => option.isoCode === countryCode
    );
    const postalCodeControl = this.onboardingForm.controls.postCode;

    postalCodeControl.setValidators(
      this.createPostalCodeValidators(country?.zipCodeValidation)
    );
    postalCodeControl.updateValueAndValidity();
  }

  private createPostalCodeValidators(
    postalCodeValidation: EZipCodeValidation | undefined
  ): ValidatorFn[] {
    switch (postalCodeValidation) {
      case EZipCodeValidation.fiveDigits:
        return [Validators.required, Validators.pattern(/^\d{5}$/)];
      case EZipCodeValidation.fourDigits:
        return [Validators.required, Validators.pattern(/^\d{4}$/)];
      case EZipCodeValidation.fourDigitsTwoLetters:
        return [Validators.required, Validators.pattern(/^\d{4}[a-zA-Z]{2}$/)];
      default:
        return [Validators.required];
    }
  }

  private resetControls(
    controlNames: Array<'postCode' | 'city' | 'street' | 'houseNumber'>
  ): void {
    controlNames.forEach(
      (controlName: 'postCode' | 'city' | 'street' | 'houseNumber'): void =>
        this.onboardingForm.controls[controlName].reset()
    );
  }

  private isAddressComplete(): boolean {
    const value = this.onboardingFormValue();
    const houseNumberIsRequired =
      this.addressValidationType() !==
      EAddressValidationType.LooseWithoutHouseNumber;

    return Boolean(
      value.country &&
      value.postCode &&
      value.city &&
      value.street &&
      (!houseNumberIsRequired || value.houseNumber)
    );
  }

  private isKnownOption<T>(
    options: T[],
    value: string | undefined,
    attribute: keyof T
  ): boolean {
    if (!value) {
      return false;
    }

    return options.some(
      (option: T): boolean =>
        String(option[attribute]).toLocaleLowerCase() ===
        value.toLocaleLowerCase()
    );
  }
}
